import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";

import { appRequest } from "routes";
import ButtonSS from "components/common/Buttons/ButtonSS";

type ModalProps = {
  isOpen: boolean;
  isCurrentPass: boolean;
  id?: string;
  onClose: () => void;
};

type LoginFormValues = {
  password: string;
  newPassword: string;
  confirmPassword: string;
};
type LoginFormErrors = {
  password?: string;
  newPassword?: string;
  confirmPassword?: string;
};

export default function ResetPasswordFormModal({
  isOpen,
  onClose,
  isCurrentPass,
  id,
}: ModalProps): JSX.Element {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState<LoginFormValues>({
    password: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<LoginFormErrors>({});

  const validatePassword = () => {
    if (!values.password) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: "Password is required",
      }));
    } else if (values.password.length < 6) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: "Password must be at least 6 characters long",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, password: undefined }));
    }
  };

  const validateNewPassword = () => {
    if (!values.newPassword) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        newPassword: "New Password is required",
      }));
    } else if (values.newPassword.length < 6) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        newPassword: "New Password must be at least 6 characters long",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, newPassword: undefined }));
    }
  };

  const validateConfirmPassword = () => {
    if (!values.confirmPassword) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: "Confirm Password is required",
      }));
    } else if (values.confirmPassword.length < 6) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: "Confirm Password must be at least 6 characters long",
      }));
    } else if (values.newPassword !== values.confirmPassword) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: "Passwords do not match",
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: undefined,
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLButtonElement>) => {
    event.preventDefault();
    validatePassword();
    validateNewPassword();
    validateConfirmPassword();

    if (
      !errors.password &&
      !errors.newPassword &&
      !errors.confirmPassword
    ) {
      let input;
      if (isCurrentPass) {
        input = {
          currentPassword: values.password,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        };
      } else {
        input = {
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
          id: id,
        };
      }
      try {
        if(input.currentPassword && input.newPassword && input.confirmPassword){
        setLoading(true);
        const response: any = await appRequest("user", "changePassword", input);
        if (response && !response.error) {
          toast({
            title: "Password Updated successfully.",
            status: "success",
            duration: 9000,
            isClosable: true,
            position: "top-right",
          });
          setValues({password: "",
          newPassword: "",
          confirmPassword: "",
        })
          onClose();
        } else {
          toast({
            title: response.error.message,
            status: "error",
            duration: 9000,
            isClosable: true,
            position: "top-right",
          });
        }
      }
      else
      {
        toast({
          title: 'Validation Error , please fill all fields',
          status: "error",
          duration: 9000,
          isClosable: true,
          position: "top-right",
        });

      }
      } catch (error) {
        toast({
          title: "Account not created.",
          description: JSON.stringify(error),
          status: "error",
          duration: 9000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "xs", md: "md" }} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          color="teal.300"
          fontSize={{ base: "lg", md: "2xl" }}
          fontWeight="700"
        >
          Enter new password
        </ModalHeader>

        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <Stack spacing={4}>
              {isCurrentPass && (
                <FormControl
                  id="currentPassword"
                  isInvalid={!!errors.password}
                  isRequired
                >
                  <FormLabel color="gray.700">Current Password</FormLabel>
                  <Input
                    type="password"
                    placeholder="Your current password"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={validatePassword}
                  />
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>
              )}
              <FormControl
                id="newPassword"
                isInvalid={!!errors.newPassword}
                isRequired
              >
                <FormLabel color="gray.700">New Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Your new password"
                  name="newPassword"
                  value={values.newPassword}
                  onChange={handleChange}
                  onBlur={validateNewPassword}
                />
                <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
              </FormControl>
              <FormControl
                id="confirmPassword"
                isInvalid={!!errors.confirmPassword}
                isRequired
              >
                <FormLabel color="gray.700">Confirm Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Re-enter your new password"
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={validateConfirmPassword}
                />
                <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
              </FormControl>
            </Stack>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <ButtonSS bg="red.500" hover={{ bg: "red.600" }} w="80px" mr={6} onClick={onClose} buttonSS="Cancel"/>
          <ButtonSS
            type="button"
            w="80px"
            onClick={handleSubmit}
            isLoading={loading}
            buttonSS="Save"
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
